import * as Urql from "urql";

import {
  AuthProvider,
  EnvProvider,
  MetricsProvider,
  SupabaseProvider,
  createMetricsCollector,
  useAuthorizedUrqlClient,
  useEnv,
  useSupabaseUser,
} from "@repo/lib-web-common";
import type { FrontendMetricInput } from "@repo/lib-web-gql";
// import { FRONTEND_ENV } from "@repo/config-env-variables";
import { OrganizationProvider, Toaster, toast } from "@repo/lib-web-ui";
// import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode, useEffect, useMemo, useRef } from "react";

import { CustomScalarResolvers } from "@repo/lib-web-gql/scalar-resolvers";
import { createRoot } from "react-dom/client";
import { graphql } from "@repo/lib-web-gql";
// Import the generated route tree and attach it to the root route
import { routeTree } from "./routeTree.gen";

// Import the generated route tree
// import { routeTree } from "./routeTree.gen";

// // Define the router context type
// interface RouterContext {
//   user: undefined | User;
//   userLoading: boolean;
// }

// // Create a root route with the typed context
// const rootRoute = createRootRouteWithContext<RouterContext>()({
//   // Root route configuration here if needed
// });

// Create the final route tree with the typed root route
const router = createRouter({
  routeTree,
  context: {
    user: { id: undefined, loading: true },
  },
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
// const queryClient = new QueryClient();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <EnvProvider
      env={
        // FRONTEND_ENV

        {
          REACT_APP_API_ENDPOINT: import.meta.env.REACT_APP_API_ENDPOINT!,
          REACT_APP_URI_CLIENT_APP: import.meta.env.REACT_APP_URI_CLIENT_APP!,
          REACT_APP_ENV: import.meta.env.REACT_APP_ENV!,
          REACT_APP_SUPABASE_URL: import.meta.env.REACT_APP_SUPABASE_URL!,
          REACT_APP_SUPABASE_ANON_KEY: import.meta.env
            .REACT_APP_SUPABASE_ANON_KEY,
          REACT_APP_HERE_MAPS_API_KEY: import.meta.env
            .REACT_APP_HERE_MAPS_API_KEY!,
          REACT_APP_MAP_TILES_BASE_URL:
            import.meta.env.REACT_APP_MAP_TILES_BASE_URL ?? "",
        }
      }
    >
      {/* <CfWorkersProvider> */}
      <SupabaseProvider>
        <AuthProvider>
          <Toaster richColors />
          <UrqlWrapper>
            <MainInner />
          </UrqlWrapper>
          {/* <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme"> */}
          {/* </ThemeProvider> */}
        </AuthProvider>
      </SupabaseProvider>
      {/* </CfWorkersProvider> */}
    </EnvProvider>
  </StrictMode>,
);

const RecordFrontendMetricsMutation = graphql(`
  mutation ApiRecordFrontendMetrics($metrics: [FrontendMetricInput!]!) {
    apiRecordFrontendMetrics(metrics: $metrics)
  }
`);

function UrqlWrapper({ children }: React.PropsWithChildren<{}>) {
  const apiEndpoint = useEnv("REACT_APP_API_ENDPOINT");

  // Track shown errors to prevent duplicates
  const shownErrors = useRef(new Set<string>());

  const urqlClient = useAuthorizedUrqlClient(apiEndpoint, {
    cacheExchangeOptions: {
      keys: {
        LatLng: () => null,
        AttributesForBoundingBoxResponse: () => null,
        BBox: ({ north, south, east, west }) =>
          `${north},${south},${east},${west}`,
      },
    },
    onError: async (error, operation) => {
      console.error(error);
      console.error(operation);

      // Telemetry (and other background) operations opt out of the global
      // error toast — their failures are internal and logged elsewhere.
      if (operation.context?.suppressErrorToast) return;

      // Create a unique key for this error to prevent duplicate toasts
      const operationName =
        operation.query.definitions.find(
          (def) => def.kind === "OperationDefinition",
        )?.name?.value || "unknown";
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const errorKey = `${operationName}-${errorMessage}`;

      if (!shownErrors.current.has(errorKey)) {
        shownErrors.current.add(errorKey);

        // Clear the error after a timeout to allow showing it again later
        setTimeout(() => {
          shownErrors.current.delete(errorKey);
        }, 5000);

        toast.error(operation.context?.errorMessage || "Error");
      }
    },
    customScalarResolvers: CustomScalarResolvers,
    mutationUpdates: {
      deleteFromOrganizationInvitationsCollection: (_r, _a, cache) => {
        cache.invalidate("Organizations");
        cache.invalidate("OrganizationInvitations");
      },
      apiCreateOrganizationInvitation: (_r, _a, cache) => {
        cache.invalidate("OrganizationInvitations");
      },
      apiAcceptOrganizationInvitation: (_r, _a, cache) => {
        cache.invalidate("Organizations");
      },
      apiCreateOrganization: (_r, _a, cache) => cache.invalidate("Profiles"),
      insertIntoOrganizationInvitationsCollection: (_r, _a, cache) => {
        cache.invalidate("Organizations");
      },
    },
  });
  const metricsCollector = useMemo(
    () =>
      createMetricsCollector({
        send: async (batch) => {
          const result = await urqlClient
            .mutation(
              RecordFrontendMetricsMutation,
              {
                metrics: batch as Array<FrontendMetricInput>,
              },
              // Suppress the global error toast — telemetry failures must stay
              // invisible to users (logged by the collector instead).
              { suppressErrorToast: true },
            )
            .toPromise();
          // urql resolves with {data, error} — promise doesn't reject on
          // GraphQL/network errors. Throw so the collector logs them.
          if (result.error) throw result.error;
        },
      }),
    [urqlClient],
  );
  useEffect(() => () => metricsCollector.dispose(), [metricsCollector]);

  return (
    <Urql.Provider value={urqlClient}>
      <MetricsProvider collector={metricsCollector}>{children}</MetricsProvider>
    </Urql.Provider>
  );
}

const MyOrganizationsQuery = graphql(`
  query MyOrganizationsQuery($id: UUID!) {
    profilesCollection(filter: { id: { eq: $id } }) {
      edges {
        node {
          id
          ...MyOrganizationsFragment
        }
      }
    }
  }
`);
function MainInner() {
  const { user, loading } = useSupabaseUser();
  const id = user?.id;
  const [{ data, ...queryRest }] = Urql.useQuery({
    query: MyOrganizationsQuery,
    pause: !id,
    variables: { id: id ?? "" },
  });

  const profile = data?.profilesCollection?.edges?.[0]?.node;

  return (
    <OrganizationProvider dbFragment={profile} loading={!data}>
      <RouterProvider
        router={router}
        context={{ user: { id, loading: loading || queryRest.fetching } }}
      />
    </OrganizationProvider>
  );
}
